/**
 * MIGeoMath — geographic + camera math helpers exposed in MIGIS terms so
 * developers never reach into getCesium()'s `Cartesian3` / `Cartographic` /
 * `HeadingPitchRoll` / `EllipsoidGeodesic` directly.
 *
 * All angles are in degrees. All coordinates are [lon, lat, height?] tuples.
 */

import { getCesium } from '../util/cesium';

const EARTH_RADIUS_M = 6_378_137;
const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

export type LonLat = [number, number];
export type LonLatAlt = [number, number, number];

export interface MICameraOrientation {
  heading: number;  // degrees, 0 = north, increases clockwise
  pitch: number;    // degrees, 0 = horizon, -90 = straight down
  roll: number;     // degrees
}

export const MIGeoMath = {
  /** Convert [lon, lat, height] → engine cartesian (getCesium().Cartesian3 when present, else { x, y, z }). */
  cartesian3FromDegrees(lon: number, lat: number, height = 0): any {
    if (getCesium()) return getCesium().Cartesian3.fromDegrees(lon, lat, height);
    const c = Math.cos(lat * D2R) * (EARTH_RADIUS_M + height);
    return {
      x: c * Math.cos(lon * D2R),
      y: c * Math.sin(lon * D2R),
      z: Math.sin(lat * D2R) * (EARTH_RADIUS_M + height),
    };
  },

  /** Convert an engine cartesian → [lon, lat, height] in degrees+meters. */
  cartographicFromCartesian3(cart: any): LonLatAlt {
    if (getCesium() && cart) {
      const c = getCesium().Cartographic.fromCartesian(cart);
      return [getCesium().Math.toDegrees(c.longitude), getCesium().Math.toDegrees(c.latitude), c.height];
    }
    const r = Math.hypot(cart.x, cart.y, cart.z);
    const lat = Math.asin(cart.z / r) * R2D;
    const lon = Math.atan2(cart.y, cart.x) * R2D;
    return [lon, lat, r - EARTH_RADIUS_M];
  },

  /** Project a [lon, lat] → screen pixel using a MISceneView (or legacy viewer). */
  worldToScreen(view: any, lonLatAlt: LonLatAlt): [number, number] | null {
    if (typeof view?.geoToScreen === 'function') return view.geoToScreen(lonLatAlt);
    return null;
  },

  /** Inverse-project a screen pixel → [lon, lat, height] using a MISceneView. */
  screenToWorld(view: any, pixel: [number, number]): LonLatAlt | null {
    if (typeof view?.screenToGeo === 'function') return view.screenToGeo(pixel);
    return null;
  },

  /** Extract heading/pitch/roll (deg) from a quaternion-like {x,y,z,w}. */
  headingPitchRollFromQuaternion(q: { x: number; y: number; z: number; w: number }): MICameraOrientation {
    const { x, y, z, w } = q;
    const sinp = 2 * (w * y - z * x);
    const pitch = Math.abs(sinp) >= 1 ? Math.sign(sinp) * 90 : Math.asin(sinp) * R2D;
    const heading = Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z)) * R2D;
    const roll = Math.atan2(2 * (w * x + y * z), 1 - 2 * (x * x + y * y)) * R2D;
    return { heading, pitch, roll };
  },

  /** Great-circle distance (m) between two [lon, lat] points (haversine). */
  ellipsoidGeodesicDistance(a: LonLat, b: LonLat): number {
    const [lon1, lat1] = a;
    const [lon2, lat2] = b;
    const dLat = (lat2 - lat1) * D2R;
    const dLon = (lon2 - lon1) * D2R;
    const sinDLat = Math.sin(dLat / 2);
    const sinDLon = Math.sin(dLon / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1 * D2R) * Math.cos(lat2 * D2R) * sinDLon * sinDLon;
    return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
  },

  /** Initial bearing (deg from north, clockwise) from a → b. */
  ellipsoidGeodesicBearing(a: LonLat, b: LonLat): number {
    const [lon1, lat1] = a;
    const [lon2, lat2] = b;
    const φ1 = lat1 * D2R;
    const φ2 = lat2 * D2R;
    const Δλ = (lon2 - lon1) * D2R;
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    return ((Math.atan2(y, x) * R2D) + 360) % 360;
  },

  /** Geographic midpoint between two [lon, lat] points. */
  midpoint(a: LonLat, b: LonLat): LonLat {
    const [lon1, lat1] = a;
    const [lon2, lat2] = b;
    const φ1 = lat1 * D2R;
    const φ2 = lat2 * D2R;
    const Δλ = (lon2 - lon1) * D2R;
    const Bx = Math.cos(φ2) * Math.cos(Δλ);
    const By = Math.cos(φ2) * Math.sin(Δλ);
    const φ3 = Math.atan2(Math.sin(φ1) + Math.sin(φ2), Math.sqrt((Math.cos(φ1) + Bx) ** 2 + By * By));
    const λ3 = lon1 * D2R + Math.atan2(By, Math.cos(φ1) + Bx);
    return [λ3 * R2D, φ3 * R2D];
  },

  /** Destination point given a start, bearing (deg), and distance (m). */
  destinationPoint(start: LonLat, bearingDeg: number, distanceM: number): LonLat {
    const δ = distanceM / EARTH_RADIUS_M;
    const θ = bearingDeg * D2R;
    const φ1 = start[1] * D2R;
    const λ1 = start[0] * D2R;
    const sinφ2 = Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ);
    const φ2 = Math.asin(sinφ2);
    const y = Math.sin(θ) * Math.sin(δ) * Math.cos(φ1);
    const x = Math.cos(δ) - Math.sin(φ1) * sinφ2;
    const λ2 = λ1 + Math.atan2(y, x);
    return [((λ2 * R2D) + 540) % 360 - 180, φ2 * R2D];
  },

  /** Sample N evenly-spaced points along a geodesic from a → b (inclusive). */
  sampleAlongGeodesic(a: LonLat, b: LonLat, samples: number): LonLat[] {
    if (samples < 2) return [a, b];
    const total = MIGeoMath.ellipsoidGeodesicDistance(a, b);
    const bearing = MIGeoMath.ellipsoidGeodesicBearing(a, b);
    const out: LonLat[] = [];
    for (let i = 0; i < samples; i++) {
      const t = i / (samples - 1);
      out.push(MIGeoMath.destinationPoint(a, bearing, total * t));
    }
    return out;
  },

  EARTH_RADIUS_M,
};

export default MIGeoMath;
